import * as React from 'react';
import { useDispatch } from 'react-redux';
import useBreakpoints from '@toolkit/util/useBreakpoints';

// Utils
import { t } from '@toolkit/util/i18n';
import { findBySelected, prop } from '@src/shared/src/util/general';
import classNames from 'classnames';

// Constants
import { ACCOMMODATION_TYPES, ENVIRONMENT, STATUS } from '@src/shared/src/const/app';
// Actions
import { setUiMobileNoScrolling } from '@pod/ui/actions';
// Models
import { HotelModel } from '@src/shared/src/models';
// Components
import HotelMainImage from './HotelMainImage';
import HotelTitle from './HotelTitle';
import { HotelRating } from './HotelRating';
import HotelDetails from './HotelDetails';
import { Navbar, TextField } from '@toolkit/ui';
import { HotelPrice } from './HotelPrice';
import { Airbnb } from '@toolkit/ui/icons/Airbnb';

// Styles
import '../styles/Hotel.scss';

type Props = {
  hotel: HotelModel;
  hotelRoomCount?: number;
  env: ENVIRONMENT;
  detailsOpened?: boolean;
  isSelectingHotel?: boolean;
  showHotelRemarksForm?: boolean;
  hotelRemarks?: string;
  onChangeHotelRemarks?: (hotelRemarks: string) => void;
  onFetchDetail: (hotelId: string) => void;
  onSelectHotel: (hotelFareIds: string[]) => void;
  onCloseDetail: (hotelId: string) => void;
};

const Hotel: React.FC<Props> = ({
  hotel,
  hotelRoomCount,
  env,
  detailsOpened = false,
  isSelectingHotel,
  showHotelRemarksForm,
  hotelRemarks = '',
  onChangeHotelRemarks,
  onFetchDetail,
  onSelectHotel,
  onCloseDetail,
}) => {
  const dispatch = useDispatch();
  const { isMd } = useBreakpoints();
  const [showDetails, setShowDetails] = React.useState(detailsOpened);

  React.useEffect(() => {
    if (hotel.detailsStatus === STATUS.FAILED) {
      setShowDetails(false);
    }
  }, [hotel.detailsStatus]);

  const toggleDetails = () => {
    if (!showDetails) {
      onFetchDetail(hotel.id);
      dispatch(setUiMobileNoScrolling(true));
    } else {
      onCloseDetail(hotel.id);
      dispatch(setUiMobileNoScrolling(false));
    }
    setShowDetails(!showDetails);
  };

  const getHotelSelectionMarkup = () => {
    if (hotel.detailsStatus === STATUS.FAILED) {
      return (
        <div className="tcp-hotel-select tcp-hotel-select-not-available">
          <i className="icon-disabled" />
          {t('hotel.text.unavailable')}
        </div>
      );
    }

    return (
      <div className="tcp-hotel-select">
        <div className="tcp-hotel-select-price">
          <HotelPrice
            price={
              env === ENVIRONMENT.SEARCH ? hotel.price : prop('price', findBySelected(hotel.fares))
            }
            env={env}
            pricePerNight={hotel.pricePerRoomNight}
            accommodationType={hotel.accommodationType}
            currencyChanged={hotel.currency !== hotel.originalCurrency}
            negotiatedRates={hotel.negotiatedRates}
            orgFavorited={hotel.orgFavorited}
            co2InKg={env === ENVIRONMENT.SEARCH ? hotel.co2InKg : undefined}
          />
        </div>
        <button
          className={classNames('button', {
            'button-light':
              showDetails || env === ENVIRONMENT.CHECKOUT || env === ENVIRONMENT.CONFIRMATION,
          })}
          onClick={toggleDetails}>
          <span>{showDetails ? t('global.close_details') : t('global.view_details')}</span>
        </button>
      </div>
    );
  };

  return (
    <div className={`tcp-hotel ${showDetails ? 'tcp-hotel-is-open-details' : ''}`}>
      {isMd && showDetails && (
        <Navbar actionRight={toggleDetails} iconRight="icon-close2">
          {t('trip.button.details')}
        </Navbar>
      )}
      <div className="tcp-hotel-inner" onClick={isMd && !showDetails ? toggleDetails : undefined}>
        <div className="tcp-hotel-image">
          <HotelMainImage
            primaryImageURL={hotel.primaryPictureURL}
            pictureURLs={hotel.pictureURLs}
          />
        </div>
        <div className="tcp-hotel-info">
          <HotelTitle hotel={hotel} />
          <div className="tcp-hotel-footer">
            {hotel.accommodationType === ACCOMMODATION_TYPES.VACATION_RENTAL && (
              <div className="tcp-hotel-airbnb">
                <Airbnb />
              </div>
            )}
            <HotelRating
              numReviews={hotel.numReviews}
              rating={hotel.originalUserRating}
              scale={hotel.originalUserRatingScale}
            />
            {isMd && (
              <div className="tcp-hotel-footer-price">
                <HotelPrice
                  price={
                    env === ENVIRONMENT.SEARCH
                      ? hotel.price
                      : prop('price', findBySelected(hotel.fares))
                  }
                  env={env}
                  accommodationType={hotel.accommodationType}
                  pricePerNight={hotel.pricePerRoomNight}
                  currencyChanged={hotel.currency !== hotel.originalCurrency}
                  negotiatedRates={hotel.negotiatedRates}
                  orgFavorited={hotel.orgFavorited}
                  co2InKg={env === ENVIRONMENT.SEARCH ? hotel.co2InKg : undefined}
                />
              </div>
            )}
          </div>
        </div>
        {!isMd && getHotelSelectionMarkup()}
      </div>
      {showDetails && (
        <div>
          <HotelDetails
            hotel={hotel}
            env={env}
            isSelectingHotel={isSelectingHotel}
            hotelRoomCount={hotelRoomCount}
            onSelectHotel={onSelectHotel}
          />
        </div>
      )}
      {showHotelRemarksForm && (
        <div className="tcp-hotel-remark">
          <TextField
            label={t('bookedItems.hotelForm.label.remarks')}
            onChange={(value) => onChangeHotelRemarks?.(value)}
            value={hotelRemarks}
          />
        </div>
      )}
    </div>
  );
};

export default Hotel;
